import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, profile } from "@/data/portfolio";
import { useActiveSection, scrollToId } from "@/hooks/usePortfolio";
import { TID } from "@/constants/testIds";

export function Navbar() {
  const { pathname, hash } = useLocation();
  const isHome = pathname === "/";
  const active = useActiveSection(navLinks.map((l) => l.id));
  const [open, setOpen] = useState(false);

  // Determine the active nav id when not on the home page.
  // Prefer the URL hash (/#about), fall back to simple pathname mappings.
  let currentActive = active;
  if (!isHome) {
    if (hash) currentActive = hash.replace("#", "");
    else if (pathname.startsWith("/case-study")) currentActive = "work";
  }

  const go = (id) => {
    if (isHome) scrollToId(id);
    setOpen(false);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl"
      data-testid={TID.nav}
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        {isHome ? (
          <button
            onClick={() => go("hero")}
            className="group flex items-center gap-2.5 font-mono text-sm"
            aria-label="Back to top"
          >
            <span className="grid h-7 w-7 place-items-center rounded-md border border-border bg-secondary/60 text-primary">
              <span className="h-2 w-2 rounded-full bg-primary pulse-dot" />
            </span>
            <span className="font-semibold tracking-tight text-foreground">{profile.first}</span>
            <span className="text-muted-foreground">/dev</span>
          </button>
        ) : (
          <Link to="/" className="group flex items-center gap-2.5 font-mono text-sm" aria-label="Back to home">
            <span className="grid h-7 w-7 place-items-center rounded-md border border-border bg-secondary/60 text-primary">
              <span className="h-2 w-2 rounded-full bg-primary pulse-dot" />
            </span>
            <span className="font-semibold tracking-tight text-foreground">{profile.first}</span>
            <span className="text-muted-foreground">/dev</span>
          </Link>
        )}

        <ul className="hidden items-center gap-8 md:flex">
          {navLinks.map((l) => (
            <li key={l.id}>
              {isHome ? (
                <button
                  data-testid={TID.navLink(l.id)}
                  onClick={() => go(l.id)}
                  className={`font-mono text-xs uppercase tracking-[0.14em] transition-colors ${
                    active === l.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-current={active === l.id ? "true" : undefined}
                >
                  {l.label}
                </button>
              ) : (
                <Link
                  data-testid={TID.navLink(l.id)}
                  to={`/#${l.id}`}
                  onClick={() => setOpen(false)}
                  className={`font-mono text-xs uppercase tracking-[0.14em] transition-colors ${
                    currentActive === l.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                  aria-current={currentActive === l.id ? "true" : undefined}
                >
                  {l.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href={profile.resume}
            download
            data-testid={TID.navResume}
            className="hidden items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-mono text-xs text-primary transition-all hover:bg-primary/20 sm:inline-flex"
          >
            Resume <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <button
            className="grid h-9 w-9 place-items-center rounded-md border border-border md:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-border/60 md:hidden"
          >
            <ul className="flex flex-col gap-1 px-5 py-4">
              {navLinks.map((l) => (
                <li key={l.id}>
                  {isHome ? (
                    <button
                      data-testid={`mobile-${TID.navLink(l.id)}`}
                      onClick={() => go(l.id)}
                      className={`w-full py-2 text-left font-mono text-sm uppercase tracking-wider transition-colors ${
                        active === l.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {l.label}
                    </button>
                  ) : (
                    <Link
                      data-testid={`mobile-${TID.navLink(l.id)}`}
                      to={`/#${l.id}`}
                      onClick={() => setOpen(false)}
                      className={`block w-full py-2 text-left font-mono text-sm uppercase tracking-wider transition-colors ${
                        currentActive === l.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                      }`}
                      aria-current={currentActive === l.id ? "true" : undefined}
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
              <a href={profile.resume} download data-testid="mobile-nav-resume-btn" className="mt-2 inline-flex items-center gap-1.5 font-mono text-sm text-primary">
                Download Resume <ArrowUpRight className="h-4 w-4" />
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
